import { useState, useMemo, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import CampanaDeAvisos from '@/components/CampanaDeAvisos';
import SiAlgoSeRompe from '@/components/SiAlgoSeRompe';
import { healthAPI } from '@/lib/api';
import {
  LayoutDashboard, BookOpen, MessageSquare, Ticket, Bot, Eye, Database, Radio, Settings, LogOut, ChevronLeft, ChevronRight, Shield, ScrollText, Users, BookUser, Boxes, BarChart3, TrendingUp, ClipboardList, Inbox, Plug, MessageCircleQuestion,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const ALL_NAV = [
  // "Mi trabajo" es lo que trabajo; "Mis solicitudes" es lo que pedi. El par se confunde, y
  // por eso llevan subtitulo en la propia pantalla.
  { to: '/mis-solicitudes', icon: Inbox, label: 'Mis solicitudes', pantalla: 'mis_solicitudes', roles: ['solicitante', 'operator', 'viewer', 'admin', 'supervisor', 'executive', 'agent'] },
  { to: '/my-work', icon: ClipboardList, label: 'Mi trabajo', pantalla: 'mi_trabajo', roles: ['operator', 'viewer', 'admin', 'supervisor', 'executive', 'agent'] },
  { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard', pantalla: 'tablero', roles: ['admin', 'supervisor', 'executive'] },
  { to: '/direccion', icon: TrendingUp, label: 'Direccion', pantalla: 'direccion', roles: ['admin', 'supervisor', 'executive'] },
  { to: '/executive', icon: BarChart3, label: 'Ejecutivo', pantalla: 'ejecutivo', roles: ['admin', 'supervisor', 'executive'] },
  { to: '/chat', icon: MessageSquare, label: 'WebChat', pantalla: 'chat', roles: ['admin', 'supervisor'] },
  { to: '/tickets', icon: Ticket, label: 'Tickets', pantalla: 'casos', roles: ['operator', 'viewer', 'admin', 'supervisor', 'executive', 'agent'] },
  // Preguntar sin abrir un caso. El solicitante no la ve: para él, preguntar *es* abrir un caso.
  { to: '/consulta', icon: MessageCircleQuestion, label: 'Consulta', pantalla: 'consulta', roles: ['operator', 'admin', 'supervisor', 'executive', 'agent'] },
  { to: '/monitor', icon: Eye, label: 'Monitor HITL', pantalla: 'monitor', roles: ['operator', 'viewer', 'admin', 'supervisor', 'executive', 'agent'] },
  { to: '/agents', icon: Bot, label: 'Agentes', pantalla: 'agentes', roles: ['admin', 'supervisor'] },
  { to: '/knowledge', icon: Database, label: 'Base Conocimiento', pantalla: 'conocimiento', roles: ['admin', 'supervisor'] },
  { to: '/channels', icon: Radio, label: 'Canales', pantalla: 'canales', roles: ['admin', 'supervisor'] },
  { to: '/conexiones', icon: Plug, label: 'Conexiones', pantalla: 'conexiones', roles: ['admin', 'supervisor'] },
  { to: '/capsules', icon: Boxes, label: 'Capsulas', pantalla: 'capsulas', roles: ['admin', 'supervisor'] },
  { to: '/team', icon: Users, label: 'Equipo', pantalla: 'equipo', roles: ['admin', 'supervisor'] },
  { to: '/personas', icon: BookUser, label: 'Personas', pantalla: 'personas', roles: ['admin', 'supervisor', 'agent', 'operator'] },
  { to: '/capsule-config', icon: Settings, label: 'Config Capsula', pantalla: 'config_capsula', roles: ['admin', 'supervisor'] },
  { to: '/logs', icon: ScrollText, label: 'Logs sistema', pantalla: 'registros', roles: ['admin', 'supervisor'] },
  { to: '/manual', icon: BookOpen, label: 'Manual', pantalla: 'manual', roles: ['admin', 'supervisor'] },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [version, setVersion] = useState(null);

  // Version del backend desplegado. Silenciosa a proposito: si /health no responde,
  // no se muestra nada. Es informacion de soporte, no debe romper el layout.
  useEffect(() => {
    let vivo = true;
    healthAPI
      .get()
      .then((r) => {
        if (vivo) setVersion(r.data?.version || null);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  const versionCorta = version?.git_sha && version.git_sha !== 'unknown' ? version.git_sha : null;
  const versionDetalle = version
    ? `OmniSyS ${version.app || '?'} · commit ${version.git_sha || 'desconocido'} · build ${version.built_at || 'desconocido'}`
    : '';

  /**
   * Qué se le ofrece a esta persona.
   *
   * **Lo declara la cápsula y viaja con la sesión** (`user.pantallas`). Antes la lista estaba
   * escrita acá y en el router, en dos lugares que podían discrepar: un ítem visible que al
   * clickearlo redirige es peor que no tenerlo.
   *
   * Si la sesión no trae `pantallas` —una pestaña abierta desde antes del deploy— manda la lista
   * de siempre, para que nadie se quede sin menú por una sesión vieja.
   */
  const navItems = useMemo(() => {
    const role = user?.role || 'operator';
    const declaradas = Array.isArray(user?.pantallas) ? user.pantallas : null;
    if (declaradas) return ALL_NAV.filter((item) => declaradas.includes(item.pantalla));
    return ALL_NAV.filter((item) => item.roles.includes(role));
  }, [user?.role, user?.pantallas]);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="flex h-screen bg-white font-body" data-testid="app-layout">
      <aside
        className={`${collapsed ? 'w-16' : 'w-56'} bg-[#09090B] flex flex-col border-r border-zinc-800 transition-all duration-200`}
        data-testid="sidebar"
      >
        <div className="h-14 flex items-center px-4 border-b border-zinc-800">
          {!collapsed && (
            <span className="font-heading text-lg font-bold text-white tracking-tighter">OMNISYS</span>
          )}
          {collapsed && (
            <span className="font-heading text-lg font-bold text-white tracking-tighter mx-auto">O</span>
          )}
        </div>

        <nav className="flex-1 py-3 space-y-0.5 px-2">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.slice(1)}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors duration-150 rounded-sm ${
                  isActive
                    ? 'bg-brand-primary text-white'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                }`
              }
            >
              <item.icon className="w-4 h-4 flex-shrink-0" />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-800 p-3">
          {!collapsed && (
            <div className="mb-3 px-2">
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-widest flex items-center gap-1">
                <Shield className="w-3 h-3" /> {user?.role}
              </p>
              <p className="text-sm text-zinc-300 truncate">{user?.full_name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.tenant_id}</p>
              {versionCorta && (
                <p
                  className="text-xs text-zinc-600 font-mono truncate mt-1"
                  title={versionDetalle}
                  data-testid="app-version"
                >
                  v{versionCorta}
                </p>
              )}
            </div>
          )}
          {/* **La campana, arriba de los botones del sistema y no entre ellos.** Vive acá y no
              en Mi Trabajo porque el contador existía sólo estando parado en esa pantalla: quien
              trabaja el día entero en Tickets no se enteraba de nada. Con el sidebar abierto
              lleva rótulo — como ícono suelto entre la flecha de colapsar y el de salir, nadie
              la encontró. */}
          <div className="mb-1">
            <CampanaDeAvisos tenantId={user?.tenant_id} compacta={collapsed} />
          </div>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              data-testid="toggle-sidebar"
              onClick={() => setCollapsed(!collapsed)}
              className="text-zinc-500 hover:text-white hover:bg-zinc-800 h-8 w-8"
            >
              {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
            {!collapsed && (
              <Button
                variant="ghost"
                size="icon"
                data-testid="logout-button"
                onClick={handleLogout}
                className="text-zinc-500 hover:text-brand-danger hover:bg-zinc-800 h-8 w-8 ml-auto"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            )}
          </div>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-brand-surface">
        {/* **El menú sobrevive a la sección.** Envuelto acá y no más arriba, un error deja la
            navegación en pie: se rompe lo que estabas mirando, no la aplicación. */}
        <SiAlgoSeRompe donde={window.location.pathname.replace('/', '') || 'esta pantalla'}>
          <Outlet />
        </SiAlgoSeRompe>
      </main>
    </div>
  );
}
